// @ts-check
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  --cb-modal-viewport-offset-top: clamp(32px, 20vh, 140px);
  --cb-modal-viewport-offset-bottom: clamp(16px, 6vh, 56px);
  padding: var(--cb-modal-viewport-offset-top) 16px
    var(--cb-modal-viewport-offset-bottom);
  overflow: hidden;
  z-index: 2000; // Needs to sit above fixed header/footer (Bootstrap fixed-top is 1030).

  @media (max-height: 768px) {
    &[data-full-height-on-small='true'] {
      --cb-modal-viewport-offset-top: 8px;
      --cb-modal-viewport-offset-bottom: 8px;
    }

    &[data-fill-height-on-small='true'] {
      align-items: stretch;
    }

    &[data-fill-height-on-small='true'] #cb-modal {
      height: calc(
        100vh - var(--cb-modal-viewport-offset-top) -
          var(--cb-modal-viewport-offset-bottom)
      );
      overflow-y: hidden;
      display: flex;
      flex-direction: column;
    }

    &[data-fill-height-on-small='true'] .cb-modal-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: var(--cb-modal-overflow-y, auto);
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
      padding-bottom: 8px;
    }
  }

  h3 {
    margin: 0 0 10px 0;
  }

  #cb-modal {
    --cb-modal-padding-inline-end: 20px;
    background: white;
    padding: var(--cb-modal-padding, 10px 20px 10px 20px);
    max-width: min(var(--cb-modal-max-width, 500px), 100%);
    width: min(100%, var(--cb-modal-max-width, 500px));
    box-sizing: border-box;
    max-height: var(
      --cb-modal-max-height,
      calc(
        100vh - var(--cb-modal-viewport-offset-top) -
          var(--cb-modal-viewport-offset-bottom)
      )
    );
    overflow-y: var(--cb-modal-overflow-y, auto);
    overflow-x: hidden;
    margin-bottom: 0;
    position: relative;
  }

  &[data-scroll-body='true'] #cb-modal {
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
  }

  &[data-scroll-body='true'] .cb-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: var(--cb-modal-overflow-y, auto);
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    padding: 0 8px 8px 8px;
  }

  .modal-header {
    align-items: center;
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    padding: 0 1.25rem 0 0;
    border-bottom: 1px solid #dee2e6;
    position: relative;
  }

  .cb-modal-body {
    overflow-x: hidden;
  }

  &[data-hide-header-border='true'] .modal-header {
    border-bottom: none;
  }

  .btn-close-x {
    background: none;
    border: none;
    cursor: pointer;
    position: static;
    margin-left: auto;
    padding: 0;
    color: inherit;
  }

  .btn-close-x .modal-close-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
  }

  .btn-close-x .modal-close-icon::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: #bfbfbf;
  }

  .btn-close-x .modal-close-icon__x {
    position: relative;
    font-size: 1.05rem;
    line-height: 1;
    color: #191919;
  }
`;

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {React.ReactNode} props.children
 * @param {string} [props.title]
 * @param {string} [props.maxWidth]
 * @param {string} [props.maxHeight]
 * @param {boolean} [props.scrollable]
 * @param {boolean} [props.showTitle]
 * @param {string} [props.padding]
 * @param {boolean} [props.hideHeaderBorder]
 * @param {boolean} [props.fullHeightOnSmallScreens]
 * @param {boolean} [props.fillHeightOnSmallScreens]
 * @param {boolean} [props.scrollBody]
 */
const Modal = ({
  isOpen,
  onClose,
  children,
  title = 'Export List',
  maxWidth = undefined,
  maxHeight = undefined,
  scrollable = true,
  showTitle = true,
  padding = undefined,
  hideHeaderBorder = false,
  fullHeightOnSmallScreens = false,
  fillHeightOnSmallScreens = false,
  scrollBody = false,
}) => {
  useEffect(() => {
    // Handle the case when isOpen changes.
    const handleEscape = (event) => {
      if (event.keyCode === 27) onClose(); // Close modal on ESC key
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    } else {
      document.removeEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Avoid unnecessary computed property keys for CSS variables.
  const styleVars = /** @type {React.CSSProperties} */ ({
    ...(maxWidth ? { '--cb-modal-max-width': maxWidth } : {}),
    ...(maxHeight ? { '--cb-modal-max-height': maxHeight } : {}),
    ...(padding ? { '--cb-modal-padding': padding } : {}),
    ...(!scrollable ? { '--cb-modal-overflow-y': 'hidden' } : {}),
  });

  return ReactDOM.createPortal(
    <Container
      data-hide-header-border={hideHeaderBorder}
      data-full-height-on-small={fullHeightOnSmallScreens}
      data-fill-height-on-small={fillHeightOnSmallScreens}
      data-scroll-body={scrollBody}
    >
      <div id="cb-modal" style={styleVars}>
        {showTitle ? (
          <div className="modal-header">
            <h3>{title}</h3>
            <button
              type="button"
              name="close-x-button"
              className="btn-close-x"
              aria-label="Close"
              onClick={onClose}
            >
              <span className="modal-close-icon" aria-hidden="true">
                <i className="fa-solid fa-xmark modal-close-icon__x" />
              </span>
            </button>
          </div>
        ) : null}
        {showTitle || scrollBody ? (
          <div className="cb-modal-body">{children}</div>
        ) : (
          children
        )}
      </div>
    </Container>,
    document.body,
  );
};

export { Modal };
